/**
 * Mastery-gated card variants (BOSS-REWORK.md §B10).
 *
 * BULLET-HELL.md §D9 wanted DoDonPachi-style rank and BOSS-REWORK.md §B10
 * argued it out: rank makes a card behave differently every time you meet it,
 * which directly attacks the learnability the whole spell-card layer exists to
 * build. This is the safer lever with the same goal — a card you have CAPTURED
 * `VARIANT_CAPTURE_THRESHOLD` times unlocks its harder form permanently.
 *
 * Deterministic (the same record always yields the same fight), visible (the
 * record screen already counts captures), and it rewards mastery instead of
 * punishing power, which is the exact inverse of what rank does.
 *
 * A variant is a shallow patch over the pattern's `config` plus optional
 * timing multipliers, so a variant is authored in numbers a boss already
 * speaks rather than as a second pattern class. Cards not listed here have no
 * variant and never change, which is the correct default: only a card whose
 * harder form is genuinely *more interesting* — not merely bigger — belongs in
 * this table.
 */

/** Captures of a card required to unlock its variant, permanently. */
export const VARIANT_CAPTURE_THRESHOLD = 5;

export const PATTERN_VARIANTS = {
  // The ring answers back: each bullet hooks, so the gaps rotate as they
  // travel and the read has to be made twice.
  'Radial Ring': { bullet: { omega: 0.5 } },
  // Shards that split once late in their flight — the volley you dodged
  // becomes the volley you have to dodge again, at a moment you can predict.
  'Homing Shard Volley': { bullet: { splitAfter: 0.9, splitInto: 3, splitSpread: 0.7 } },
  // A faster sweep on a shorter warning: the lane still exists, you just have
  // less time to be in it.
  'Sweeping Laser': { config: { sweepSpeed: 1.35 }, telegraphMultiplier: 0.8 },
  // One more row of the board to read, at the same speed.
  'Bullet Board': { config: { speed: 1.12 } },
  // The implosion arrives held: the ring hangs where it formed before
  // collapsing inward, which is scarier and fairer at once.
  'Implosion': { bullet: { holdFor: 0.35 } },
  // The grid gains a column. The safe cells are narrower, not fewer.
  'Grid Barrage': { config: { cols: 1 } },
  // A shorter wind-up on the charge — read it by the boss's posture, not by
  // the warning line.
  'Telegraphed Charge': { telegraphMultiplier: 0.7 },
  // Skyfall's markers curve as they fall, so standing on yesterday's safe spot
  // stops working.
  'Skyfall': { bullet: { omega: -0.4 } },
};

/**
 * Applies a variant patch to a live pattern. Returns true if anything changed.
 *
 * `config` entries are ADDED for numeric keys the pattern already has (a
 * variant says "one more column", "12% faster"), which keeps a single table
 * correct across every boss's ATTACK_CONFIG row instead of hard-coding one
 * boss's absolute numbers. `bullet` is a per-bullet motion patch the pattern
 * itself never has to know about — SpawningSystem hands it to the pool as a
 * spawn overlay (see `BulletPool.spawnOverlay`).
 */
export function applyVariant(pattern, variant) {
  if (!pattern || !variant) return false;
  if (variant.config) {
    pattern.config = { ...pattern.config };
    for (const [key, delta] of Object.entries(variant.config)) {
      const current = pattern.config[key];
      // Fractions multiply a speed, integers add a row — distinguished by the
      // value itself so the table reads the way a designer would say it.
      pattern.config[key] = typeof current === 'number'
        ? (Number.isInteger(delta) ? current + delta : current * delta)
        : delta;
    }
  }
  if (variant.telegraphMultiplier) {
    pattern.telegraphDuration *= variant.telegraphMultiplier;
  }
  if (variant.bullet) pattern.bulletOverlay = { ...(pattern.bulletOverlay ?? {}), ...variant.bullet };
  pattern.variantActive = true;
  return true;
}

/**
 * The variant for `name`, or null — including null when the player hasn't
 * earned it. `records` is SaveManager's `statistics.patternRecords` shape
 * (`{ [name]: { attempts, captures } }`); passing nothing means no variants,
 * which is what every bench and test that doesn't care about them gets.
 */
export function unlockedVariant(name, records = null) {
  const variant = PATTERN_VARIANTS[name];
  if (!variant || !records) return null;
  const captures = records[name]?.captures ?? 0;
  return captures >= VARIANT_CAPTURE_THRESHOLD ? variant : null;
}
